import React, { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import { CircularProgress } from "@material-ui/core";
import { Skeleton } from "@material-ui/lab";
import _ from "lodash";
import FeedList from "../Feeds/FeedList";
import companyPublicView from "../../services/companyPublicView.services";
import NotFoundPage from "../../components/common/NotFoundPage";

const PAGE_SIZE = 10;

const CompanyPosts = () => {
  const { companySlug = "" } = useParams<{ companySlug: string }>();
  const location = useLocation();
  const [initialLoading, setInitialLoading] = useState(true);
  const [isFetchingMore, setIsFetchingMore] = useState(false);
  const [feeds, setFeeds] = useState<any[]>([]);
  const [companyId, setCompanyId] = useState<number | null>(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const isFetchingRef = useRef(false);
  const [headerUser, setHeaderUser] = useState<{
    name?: string;
    profile_image?: string;
    isCompany?: boolean;
  }>({});

  const fetchFeedsPage = useCallback(
    async (id: number, pageNum: number, append: boolean) => {
      const feedsRes: any = await companyPublicView.getCompanyFeeds({
        companyId: id,
        page: pageNum,
        pageSize: PAGE_SIZE,
        postType: 0,
      });

      const raw =
        feedsRes?.flag === false ? [] : _.get(feedsRes, "data", []);
      const list = Array.isArray(raw) ? raw : [];
      const pagination = feedsRes?.pagination ?? {};

      setTotalPages(pagination.totalPages ?? 0);
      setFeeds((prev) => (append ? [...prev, ...list] : list));

      return list;
    },
    []
  );

  useEffect(() => {
    let cancelled = false;

    const loadInitial = async () => {
      if (!companySlug) {
        if (!cancelled) {
          setFeeds([]);
          setCompanyId(null);
          setInitialLoading(false);
        }
        return;
      }

      setInitialLoading(true);
      setPage(1);
      setFeeds([]);
      setTotalPages(0);

      try {
        const profileRes: any = await companyPublicView.getCompanyProfile({
          companySlug,
        });
        if (cancelled) return;

        const profile = profileRes?.flag ? profileRes.data : {};
        const id = profile?.id;

        setHeaderUser({
          name: _.get(profile, "companyProfile.company_name", ""),
          profile_image: _.get(profile, "companyProfile.company_logo", ""),
          isCompany: true,
        });

        if (!id) {
          setCompanyId(null);
          setFeeds([]);
          return;
        }

        setCompanyId(id);
        await fetchFeedsPage(id, 1, false);
      } catch {
        if (!cancelled) {
          setFeeds([]);
          setCompanyId(null);
        }
      } finally {
        if (!cancelled) setInitialLoading(false);
      }
    };

    loadInitial();

    return () => {
      cancelled = true;
    };
  }, [companySlug, fetchFeedsPage, location.key]);

  useEffect(() => {
    if (page === 1 || !companyId) return;

    let cancelled = false;

    const loadMore = async () => {
      setIsFetchingMore(true);
      isFetchingRef.current = true;
      try {
        await fetchFeedsPage(companyId, page, true);
      } finally {
        if (!cancelled) {
          setIsFetchingMore(false);
          isFetchingRef.current = false;
        }
      }
    };

    loadMore();

    return () => {
      cancelled = true;
    };
  }, [page, companyId, fetchFeedsPage]);

  useEffect(() => {
    isFetchingRef.current = isFetchingMore;
  }, [isFetchingMore]);

  useEffect(() => {
    const handleScroll = _.debounce(() => {
      const { bottom } = document.documentElement.getBoundingClientRect();
      const reachedBottom = window.innerHeight > bottom - 200;

      if (
        reachedBottom &&
        !isFetchingRef.current &&
        !initialLoading &&
        page < totalPages
      ) {
        setPage((prev) => prev + 1);
      }
    }, 100);

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [page, totalPages, initialLoading]);

  if (initialLoading) {
    return (
      <div className="max-container">
        <div className="feed-container">
          {[1, 2, 3].map((item) => (
            <Skeleton
              key={`company-post-skeleton-${item}`}
              variant="rect"
              width="100%"
              height={180}
              style={{ marginBottom: 16 }}
            />
          ))}
        </div>
      </div>
    );
  }

  if (_.isEmpty(feeds)) {
    return (
      <div className="max-container">
        <div className="feed-container">
          <NotFoundPage variant="posts" />
        </div>
      </div>
    );
  }

  return (
    <div className="max-container">
      <div className="feed-container">
        <FeedList
          readOnly={true}
          feeds={feeds}
          headerUser={headerUser}
          fetchOnScroll={false}
        />
        {isFetchingMore && (
          <CircularProgress color="secondary" className="circular-progress" />
        )}
      </div>
    </div>
  );
};

export default CompanyPosts;
